import * as lamejs from 'lamejs';
import { getUserMedia } from '../window';
import AudioContextFactory from './audio-context-factory';
import { destroyMediaStream } from '../mediastream';

// for more information visit https://aws.nlited.org/p599.htm

const STROKE_COLOR = '#0096ef';

const CHANNEL_CONFIG = {
  channelCount: 1, // 1=mono, 2=stereo
  bufferSize: 4096, // input buffer size (bytes), if 0, then the implementation will choose the best buffer size for the given environment.
  sampleRate: 44100, // Input sample rate (samples per second)
  bitRate: 128,
};

const MAX_INT = 32767;

export const createRecorder = async (canvas, width, height) => {
  return new Promise((resolve, reject) => {
    getUserMedia({
      audio: true,
      video: false,
    })
      .then((stream) => {
        if (!canvas) reject('Invalid canvas dom element.');

        const canvasCtx = canvas.getContext('2d');

        let gainStreamMP3 = [];

        let analyser, input, gainNode, bufferLength, dataArray;

        let isRecording = false;
        let gainEncoder;

        let drawVisual;

        clearCanvas();

        const cleanup = () => {
          try {
            gainNode.onaudioprocess = null;
            input.disconnect(analyser);
            input.disconnect(gainNode);
            gainNode.disconnect();
            destroyMediaStream(stream);
          } catch (e) {
            // Do-nothing;
          }
        };

        const disconnect = () => {
          if (!isRecording) return;

          cleanup();

          input = undefined;
          gainNode = undefined;
          analyser = undefined;
          gainEncoder = undefined;

          gainStreamMP3 = [];
          clearCanvas();
        };

        const start = async () => {
          isRecording = true;
          const audioContextOptions = { sampleRate: CHANNEL_CONFIG.sampleRate };
          const audioContext = AudioContextFactory.getOrCreate('recorder', audioContextOptions);
          analyser = audioContext.createAnalyser();
          analyser.fftSize = 1024;

          input = audioContext.createMediaStreamSource(stream);
          input.connect(analyser);

          bufferLength = analyser.frequencyBinCount;
          dataArray = new Uint8Array(bufferLength);
          analyser.getByteTimeDomainData(dataArray);

          gainEncoder = new lamejs.Mp3Encoder(
            CHANNEL_CONFIG.channelCount,
            CHANNEL_CONFIG.sampleRate,
            CHANNEL_CONFIG.bitRate
          );

          const creator = input.context.createScriptProcessor || input.createJavaScriptNode;
          gainNode = creator.call(
            input.context,
            CHANNEL_CONFIG.bufferSize,
            CHANNEL_CONFIG.channelCount,
            CHANNEL_CONFIG.channelCount
          );

          input.connect(gainNode);
          gainNode.connect(input.context.destination);

          gainNode.onaudioprocess = (e) => {
            const inBuf = e.inputBuffer;
            const samples = inBuf.getChannelData(0);
            const samples16 = convertFloatToInt16(samples);
            if (samples16.length > 0) {
              // Encode PCM to mp3
              const mp3buf = gainEncoder.encodeBuffer(samples16);
              const mp3Ct = mp3buf.length;
              if (mp3Ct > 0) {
                // Add buffer to in-memory output stream.
                gainStreamMP3.push(mp3buf);
              }
            }
          };

          clearCanvas();
          draw();
        };

        const stop = () => {
          const mp3 = gainEncoder.flush();

          if (mp3.length > 0) gainStreamMP3.push(mp3);

          cleanup();
          clearCanvas();

          return {
            audioBlob: new Blob(gainStreamMP3, { type: 'audio/mp3' }),
            originalFilename: new Date().toISOString(),
          };
        };

        function clearCanvas() {
          cancelAnimationFrame(drawVisual);
          canvasCtx.clearRect(0, 0, width, height);
          canvasCtx.fillStyle = '#fff';
          canvasCtx.fillRect(0, 0, width, height);
          canvasCtx.lineWidth = 2;
          canvasCtx.strokeStyle = STROKE_COLOR;
          canvasCtx.beginPath();
          canvasCtx.moveTo(0, height / 2);
          canvasCtx.lineTo(canvas.width, canvas.height / 2);
          canvasCtx.stroke();
        }

        function draw() {
          cancelAnimationFrame(drawVisual);
          drawVisual = requestAnimationFrame(draw);
          analyser.getByteTimeDomainData(dataArray);
          canvasCtx.fillStyle = '#fff';
          canvasCtx.fillRect(0, 0, width, height);
          canvasCtx.lineWidth = 2;
          canvasCtx.strokeStyle = STROKE_COLOR;
          canvasCtx.beginPath();
          const sliceWidth = (width * 1.0) / bufferLength;

          let x = 0;

          for (let i = 0; i < bufferLength; i++) {
            const v = dataArray[i] / 128.0;
            const y = (v * height) / 2;
            if (i === 0) {
              canvasCtx.moveTo(x, y);
            } else {
              canvasCtx.lineTo(x, y);
            }

            x += sliceWidth;
          }

          canvasCtx.lineTo(canvas.width, canvas.height / 2);
          canvasCtx.stroke();
        }

        resolve({
          start,
          stop,
          disconnect,
        });
      })
      .catch((err) => reject(err.message));
  });
};

function convertFloatToInt16(inFloat) {
  let sampleCt = inFloat.length;
  let outInt16 = new Int16Array(sampleCt);
  for (let n1 = 0; n1 < sampleCt; n1++) {
    // This is where I can apply waveform modifiers.
    let sample16 = 0x8000 * inFloat[n1];

    // Clamp value to avoid integer overflow, which causes audible pops and clicks.
    if (sample16 < -MAX_INT) sample16 = -MAX_INT;
    else if (sample16 > MAX_INT) sample16 = MAX_INT;

    outInt16[n1] = sample16;
  }

  return outInt16;
}
